import { Component, effect, inject, input, output } from '@angular/core';
import type { ColumnIdentification, TableSettingsConfig } from '../../data/table-column-config';
import { MatButton } from '@angular/material/button';
import { Store } from '@ngxs/store';
import { ChangeColumnVisibilityAction } from '../../state/action/change-column-visibility.action';
import { MatCheckbox } from '@angular/material/checkbox';
import { TranslocoDirective } from '@jsverse/transloco';
import { I18nProviderInjectionToken } from '../../i18n/i18n.scope';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ResolveTranslateAbleTextPipe } from '@pct-ng/i18n';

interface CheckBoxConfig {
  display: boolean;
  label: ColumnIdentification['label'];
  id: ColumnIdentification['id'];
}

type FormType<T> = { [K in keyof T]: FormControl<T[K]> };
type ColumnFormGroup = FormType<CheckBoxConfig>;

@Component({
  selector: 'pct-feature-table-material-settings-overlay',
  imports: [
    MatButton,
    MatCheckbox,
    TranslocoDirective,
    ReactiveFormsModule,
    ResolveTranslateAbleTextPipe,
  ],
  templateUrl: './settings-overlay.component.html',
  host: { class: 'block bg-white border-px rounded-md p-[1rem] shadow-xl' },
})
export class SettingsOverlayComponent {
  protected readonly i18nScope = inject<string>(I18nProviderInjectionToken);
  #store = inject(Store);

  readonly tableId = input.required<string>();
  readonly columns = input.required<ColumnIdentification[]>();
  readonly displayedColumns = input.required<string[]>();
  readonly config = input.required<TableSettingsConfig>();

  readonly finished = output<void>();

  protected formArray!: FormArray<FormGroup<ColumnFormGroup>>;

  constructor() {
    effect(() => {
      this.formArray = new FormArray(
        this.columns().map(
          column =>
            new FormGroup<ColumnFormGroup>({
              display: new FormControl(this.#isColumnDisplayed(column.id), { nonNullable: true }),
              label: new FormControl(column.label, { nonNullable: true }),
              id: new FormControl(column.id, { nonNullable: true }),
            }),
        ) as FormGroup<ColumnFormGroup>[],
      );
      this.formArray.controls.forEach(group => {
        if (this.#isColumnMustBeShowed(group.controls.id.value)) {
          group.controls.display.disable();
        }
      });
    });
  }

  protected save(): void {
    if (this.#hasChange()) {
      this.#store.dispatch(
        new ChangeColumnVisibilityAction({
          tableId: this.tableId(),
          displayedColumns: this.#getDisplayedColumnIds(),
        }),
      );
    }

    this.finished.emit();
  }

  #isColumnDisplayed(columnId: string): boolean {
    return this.displayedColumns().includes(columnId);
  }

  #isColumnMustBeShowed(columnId: string): boolean {
    return this.config().mustShowColumns.includes(columnId);
  }

  #hasChange(): boolean {
    const selectedColumnIds = this.#getDisplayedColumnIds();
    if (selectedColumnIds.length !== this.displayedColumns().length) {
      return true;
    }

    return !this.displayedColumns().every(columnId => selectedColumnIds.includes(columnId));
  }

  #getDisplayedColumnIds(): string[] {
    const groups = this.formArray.getRawValue();
    return groups.filter(group => group.display).map(columnConfig => columnConfig.id!);
  }
}
